<template>
    <!-- Remove v-on:after-leave -->
    <transition name="fade">
        <div v-if="notification.visible" :class="toast_classes">
            {{ notification.message }}
        </div>
    </transition>
</template>

<script setup>
// 1. Import onBeforeUnmount
import { computed, onMounted, onBeforeUnmount } from 'vue'

// Assuming useNotificationsStore is auto-imported by Nuxt or explicitly imported
// import { useNotificationsStore } from '@/stores/notifications'; // Example explicit import

const notifications_store = useNotificationsStore()
// Destructure only what's needed directly in setup if preferred, but using the store directly is fine
const { hideNotification } = notifications_store

const props = defineProps({
    notification: {
        type: Object, // Good practice to define the type
        required: true, // If it's always expected
    },
})

// Renamed to avoid confusion with the store function if destructured
const hideThisNotification = (notification) => {
    // Directly call the store action
    hideNotification(notification)
}

// 2. Store the timeout ID
let timeoutId = null

onMounted(() => {
    // Assign the ID
    timeoutId = setTimeout(() => {
        // Ensure props.notification is still valid if needed, though usually okay here
        if (props.notification) {
            hideThisNotification(props.notification)
        }
    }, 1600)
})

// 3. Add onBeforeUnmount to clear the timeout
onBeforeUnmount(() => {
    if (timeoutId) {
        clearTimeout(timeoutId)
    }
})

// Computed property remains the same
let toast_classes = computed(() => {
    // Defensive check in case notification is briefly null/undefined during updates
    let n = props.notification
    if (!n) return 'toast toast-primary' // Default or handle appropriately

    switch (n.level) {
        case 'info':
            return 'toast toast-primary'
        case 'success':
            return 'toast toast-success'
        case 'warning':
            return 'toast toast-warning'
        case 'error':
            return 'toast toast-error'
        default:
            return 'toast toast-primary'
    }
})
</script>

<style scoped>
/* Styles remain the same */
.toast {
    width: 100%;
    margin: 0 auto;
    background-color: var(--color-primary);

    font-size: 1.1rem;
    padding: 1em 1rem;
}

.toast-warning {
    background-color: var(--color-warning);
}

.toast-error {
    background-color: var(--color-error);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

/* Note: .fade-enter is deprecated in Vue 3 transitions, use .fade-enter-from */
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
/* Optional: Define the active state if needed, though opacity transition often covers it */
.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}
</style>
